import {
  Building2,
  Calendar as CalendarIcon,
  ClipboardList,
  Clock,
  FilePlus2,
  MoreHorizontal,
  Search,
  Shield,
  type LucideIcon,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { NavLink, Navigate, Outlet } from 'react-router-dom';

import { availableModes, resolveActiveMode, useMyProfile } from '@/features/profile';
import { AppHeaderBand } from '@/shared/ui/AppHeaderBand';
import { useUiStore } from '@/stores/ui.store';

interface Tab {
  to: string;
  labelKey: string;
  Icon: LucideIcon;
}

/** Tab set per mode (Phase 6 D-014). Universal tabs (calendar, profile) always append. */
const TABS_BY_MODE: Record<'freelance' | 'appointed' | 'admin', Tab[]> = {
  freelance: [
    { to: '/browse', labelKey: 'nav.browse', Icon: Search },
    { to: '/availability', labelKey: 'nav.availability', Icon: Clock },
    { to: '/applications', labelKey: 'nav.applications', Icon: FilePlus2 },
  ],
  appointed: [
    { to: '/masjids', labelKey: 'nav.masjids', Icon: Building2 },
    { to: '/requests', labelKey: 'nav.requests', Icon: ClipboardList },
  ],
  admin: [{ to: '/admin', labelKey: 'nav.admin', Icon: Shield }],
};

/**
 * Universal tabs (Phase 7 revamp): the profile tab moved into the "More"
 * menu so the persistent Support affordance can live in the tabbar without
 * crowding it. Support is reachable from every role's shell via /more.
 */
const UNIVERSAL_TABS: Tab[] = [
  { to: '/calendar', labelKey: 'nav.calendar', Icon: CalendarIcon },
  { to: '/more', labelKey: 'nav.more', Icon: MoreHorizontal },
];

/**
 * Authenticated shell: role-based bottom navigation + routed content. In
 * multi-role accounts (D-014), the user picks their active mode via the role
 * switcher; tabs are the union of that mode's tabs + universal tabs (calendar,
 * profile). Fresh multi-role users get an auto-picked mode until they choose.
 */
export function AppShell(): JSX.Element | null {
  const { t } = useTranslation();
  const profile = useMyProfile().data;
  const chosenMode = useUiStore((s) => s.activeMode);
  const setActiveMode = useUiStore((s) => s.setActiveMode);
  if (!profile) return null;

  const modes = availableModes(profile);
  const active = resolveActiveMode(profile, chosenMode);
  const modeTabs = active ? TABS_BY_MODE[active] : [];
  const tabs: Tab[] = [...modeTabs, ...UNIVERSAL_TABS];

  return (
    <div className="shell">
      <AppHeaderBand />
      {modes.length > 1 ? (
        <div className="mode-switcher" role="tablist" aria-label={t('nav.switchMode')}>
          {modes.map((mode) => (
            <button
              key={mode}
              type="button"
              role="tab"
              aria-selected={active === mode}
              className={`mode-switcher__pill${active === mode ? ' mode-switcher__pill--active' : ''}`}
              onClick={() => setActiveMode(mode)}
            >
              {t(`nav.mode_${mode}`)}
            </button>
          ))}
        </div>
      ) : null}
      <Outlet />
      <nav className="tabbar" aria-label={t('nav.profile')}>
        {tabs.map((tab) => (
          <NavLink
            key={tab.to}
            to={tab.to}
            className="tabbar__item"
            style={{ textDecoration: 'none' }}
          >
            <tab.Icon size={20} aria-hidden />
            <span>{t(tab.labelKey)}</span>
          </NavLink>
        ))}
      </nav>
    </div>
  );
}

/** Sends the user to the first tab of their active mode. */
export function HomeRedirect(): JSX.Element | null {
  const profile = useMyProfile().data;
  const chosenMode = useUiStore((s) => s.activeMode);
  if (!profile) return null;
  const active = resolveActiveMode(profile, chosenMode);
  const first = active ? TABS_BY_MODE[active][0]?.to : null;
  return <Navigate to={first ?? '/profile'} replace />;
}
